import {
  type AreaPermissao,
  type ChavePermissao,
  temAlgumaPermissao,
  temPermissao,
  temPermissaoNaArea,
} from '@/shared/utils/permissoesEquipeInterna';
import { useAppSelector } from '@/store/hooks';

interface PermissoesDoUsuario {
  pode: (chave: ChavePermissao) => boolean;
  podeAlguma: (chaves: readonly ChavePermissao[]) => boolean;
  podeNaArea: (...areas: readonly AreaPermissao[]) => boolean;
  /**
   * A pessoa é admin-master (`tipoUsuario === 'ADMIN'`). Só para as ações da Secretaria que NÃO
   * têm chave no catálogo (escritas do financeiro, upload de documento, liberar sentença, cancelar
   * certidão, intimação da sentença, "tentar de novo", vincular situação): o core as reserva ao
   * admin-master e nega para toda `SECRETARIA` (decisão de produto de 28/10/2026). Ação com chave
   * usa `pode`/`podeAlguma` — o `ADMIN` já recebe o catálogo inteiro em `permissoes`.
   */
  ehAdmin: boolean;
}

/**
 * Permissões individuais da equipe interna do usuário logado (`state.auth.user.permissoes`).
 * É a camada de "esconder o que o core nega": quem decide continua sendo o backend, que devolve
 * 403 sem a chave.
 *
 * @example const { pode } = usePermissao(); if (pode('processos.reabrir')) …
 */
export function usePermissao(): PermissoesDoUsuario {
  const user = useAppSelector((state) => state.auth.user);

  return {
    pode: (chave) => temPermissao(user, chave),
    podeAlguma: (chaves) => temAlgumaPermissao(user, chaves),
    podeNaArea: (...areas) => temPermissaoNaArea(user, ...areas),
    // Única leitura de `tipoUsuario` desta camada: a ação sem chave não tem lista para consultar.
    ehAdmin: user?.tipoUsuario === 'ADMIN',
  };
}
